import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from './store/slices/authSlice';
import { setFeatureFlags } from './store/slices/uiSlice';
import tgApi from './utils/tgApi';
import { createAppTheme } from './theme/theme';

// 레이아웃
import PublicLayout from './components/layout/PublicLayout';
import AdminLayout from './components/layout/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

// 공개 페이지 (즉시 로드: 랜딩만)
import LandingPage from './pages/public/SportsProLanding';
import NotFoundPage from './pages/NotFoundPage';

// 공개 페이지 (코드 스플리팅 - 필요 시 로드)
const LoginPage = lazy(() => import('./pages/public/LoginPage'));
const BlogPage = lazy(() => import('./pages/public/BlogPage'));

// 관리자 페이지 (코드 스플리팅 - 필요 시 로드)
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const QASettingsPage = lazy(() => import('./pages/admin/QASettingsPage'));
const BotSettingsPage = lazy(() => import('./pages/admin/BotSettingsPage'));
const BotsPage = lazy(() => import('./pages/admin/BotsPage'));
const AnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'));
const ContactsPage = lazy(() => import('./pages/admin/ContactsPage'));
const SiteSettingsPage = lazy(() => import('./pages/admin/SiteSettingsPage'));
const SecurityLogsPage = lazy(() => import('./pages/admin/SecurityLogsPage'));
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage'));
const ConsultationPage = lazy(() => import('./pages/admin/ConsultationPage'));
const AutoReplyLogsPage = lazy(() => import('./pages/admin/AutoReplyLogsPage'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress sx={{ color: '#d4af37' }} />
  </Box>
);

function App() {
  const dispatch = useDispatch();
  const { darkMode, featureFlags } = useSelector((state) => state.ui);
  const { userInfo } = useSelector((state) => state.auth);

  // 권한 플래그 로드 (메인관리자가 관리자에게 연락처 메뉴를 열어줬는지)
  useEffect(() => {
    if (!userInfo) return;
    tgApi.getPublicSettings()
      .then((res) => dispatch(setFeatureFlags({
        contactsAccessUserdb: res.data?.contacts_access_userdb === 'true',
        analyticsAccessUserdb: res.data?.analytics_access_userdb === 'true',
      })))
      .catch(() => dispatch(setFeatureFlags({}))); // 실패해도 flagsLoaded=true (캐시값 사용)
  }, [userInfo, dispatch]);

  const { flagsLoaded } = useSelector((state) => state.ui);
  const isAdmin = userInfo?.role === 'admin';
  const contactsRoles = featureFlags?.contactsAccessUserdb ? ['admin', 'userdb'] : ['admin'];
  const analyticsRoles = featureFlags?.analyticsAccessUserdb ? ['admin', 'userdb'] : ['admin'];
  // 권한 플래그 로드 전에는 비관리자에게 리다이렉트 대신 로더 표시 (새로고침 시 튕김 방지)
  const gated = (roles, node) => (!isAdmin && !flagsLoaded
    ? <PageLoader />
    : <ProtectedRoute roles={roles}>{node}</ProtectedRoute>);

  // 세션 타임아웃 체크 (12시간)
  useEffect(() => {
    if (!userInfo) return;
    const SESSION_TIMEOUT = 12 * 60 * 60 * 1000;
    const checkSession = () => {
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime) {
        const elapsed = Date.now() - parseInt(loginTime);
        if (elapsed >= SESSION_TIMEOUT) {
          dispatch(logout());
          alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        }
      }
    };
    const interval = setInterval(checkSession, 60 * 1000);
    return () => clearInterval(interval);
  }, [userInfo, dispatch]);

  const theme = createAppTheme(darkMode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* 공개 페이지 */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/b/:slug" element={<LandingPage />} />
          <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogPage /></Suspense>} />
          <Route path="/nklogin" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* 관리자/회원 페이지 (코드 스플리팅) */}
        <Route
          path="/nkadmin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/nkadmin/consultations" replace />} />
          {/* 상담관리: 모든 역할 */}
          <Route path="consultations" element={<Suspense fallback={<PageLoader />}><ConsultationPage /></Suspense>} />
          {/* Q&A 설정: 메인관리자 + 관리자(userdb) */}
          <Route path="qa" element={<ProtectedRoute roles={['admin', 'userdb']}><Suspense fallback={<PageLoader />}><QASettingsPage /></Suspense></ProtectedRoute>} />
          <Route path="auto-replies" element={<ProtectedRoute roles={['admin', 'userdb']}><Suspense fallback={<PageLoader />}><AutoReplyLogsPage /></Suspense></ProtectedRoute>} />
          {/* 봇 관리/봇 설정: 메인관리자 전용 */}
          <Route path="bots" element={<ProtectedRoute roles={['admin']}><Suspense fallback={<PageLoader />}><BotsPage /></Suspense></ProtectedRoute>} />
          <Route path="analytics" element={gated(analyticsRoles, <Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>)} />
          <Route path="contacts" element={gated(contactsRoles, <Suspense fallback={<PageLoader />}><ContactsPage /></Suspense>)} />
          <Route path="bot" element={<ProtectedRoute roles={['admin']}><Suspense fallback={<PageLoader />}><BotSettingsPage /></Suspense></ProtectedRoute>} />
          {/* admin 전용 */}
          <Route path="dashboard" element={<ProtectedRoute adminOnly><Suspense fallback={<PageLoader />}><DashboardPage /></Suspense></ProtectedRoute>} />
          <Route path="site-settings" element={<ProtectedRoute adminOnly><Suspense fallback={<PageLoader />}><SiteSettingsPage /></Suspense></ProtectedRoute>} />
          <Route path="security-logs" element={<ProtectedRoute adminOnly><Suspense fallback={<PageLoader />}><SecurityLogsPage /></Suspense></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute adminOnly><Suspense fallback={<PageLoader />}><UserManagementPage /></Suspense></ProtectedRoute>} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

export default App;
